import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useClient } from '../context/ClientContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Users, Eye, Heart, MessageCircle, Share2, BarChart3, Plus, Pencil, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = '';

const NETWORKS = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2', bgClass: 'bg-blue-500/10 border-blue-500/20' },
  { id: 'instagram', label: 'Instagram', color: '#E4405F', bgClass: 'bg-pink-500/10 border-pink-500/20' },
  { id: 'tiktok', label: 'TikTok', color: '#000000', bgClass: 'bg-zinc-500/10 border-zinc-500/20' },
  { id: 'youtube', label: 'YouTube', color: '#FF0000', bgClass: 'bg-red-500/10 border-red-500/20' },
  { id: 'x', label: 'X (Twitter)', color: '#1DA1F2', bgClass: 'bg-sky-500/10 border-sky-500/20' },
];

const METRIC_ICONS = {
  followers: Users,
  reach: Eye,
  engagement: Heart,
  comments: MessageCircle,
  shares: Share2,
  impressions: Eye,
};

const formatNum = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
};

const SocialInsightsPage = () => {
  const { selectedClientId, selectedClient, clients } = useClient();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('all');
  const [form, setForm] = useState({
    client_id: '', network: 'instagram', period: '', followers: '', reach: '',
    impressions: '', engagement: '', engagement_rate: '', comments: '', shares: '',
    likes: '', clicks: '', profile_visits: '', new_followers: '', posts_published: '',
  });

  const token = localStorage.getItem('token');

  useEffect(() => { fetchInsights(); }, [selectedClientId]);

  const fetchInsights = async () => {
    try {
      const cid = selectedClientId !== 'all' ? selectedClientId : '';
      const res = await fetch(`${API_URL}/api/social-insights?client_id=${cid}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setInsights(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cid = form.client_id || (selectedClientId !== 'all' ? selectedClientId : '');
    if (!cid) { toast.error('Selecciona un cliente'); return; }
    const payload = { ...form, client_id: cid };
    // Convert string fields to numbers
    ['followers','reach','impressions','engagement','comments','shares','likes','clicks','profile_visits','new_followers','posts_published'].forEach(k => {
      payload[k] = payload[k] ? parseInt(payload[k]) : 0;
    });
    payload.engagement_rate = payload.engagement_rate ? parseFloat(payload.engagement_rate) : 0;

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_URL}/api/social-insights/${editingId}` : `${API_URL}/api/social-insights`;
      await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      toast.success(editingId ? 'Insight actualizado' : 'Insight creado');
      setDialogOpen(false);
      resetForm();
      fetchInsights();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/social-insights/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      toast.success('Eliminado'); fetchInsights();
    } catch { toast.error('Error'); }
  };

  const handleEdit = (insight) => {
    setEditingId(insight.insight_id);
    setForm({
      client_id: insight.client_id, network: insight.network, period: insight.period,
      followers: insight.followers?.toString() || '', reach: insight.reach?.toString() || '',
      impressions: insight.impressions?.toString() || '', engagement: insight.engagement?.toString() || '',
      engagement_rate: insight.engagement_rate?.toString() || '', comments: insight.comments?.toString() || '',
      shares: insight.shares?.toString() || '', likes: insight.likes?.toString() || '',
      clicks: insight.clicks?.toString() || '', profile_visits: insight.profile_visits?.toString() || '',
      new_followers: insight.new_followers?.toString() || '', posts_published: insight.posts_published?.toString() || '',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ client_id: '', network: 'instagram', period: '', followers: '', reach: '', impressions: '', engagement: '', engagement_rate: '', comments: '', shares: '', likes: '', clicks: '', profile_visits: '', new_followers: '', posts_published: '' });
  };

  const filteredInsights = insights.filter(i => selectedNetwork === 'all' || i.network === selectedNetwork);

  // Aggregate stats per network
  const networkAggregates = NETWORKS.map(net => {
    const items = insights.filter(i => i.network === net.id);
    const latest = items[0]; // Most recent
    return { ...net, latest, count: items.length };
  }).filter(n => n.count > 0);

  if (loading) return <DashboardLayout title="Insights de Redes Sociales"><div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500"></div></div></DashboardLayout>;

  return (
    <DashboardLayout title="Insights de Redes Sociales">
      <div className="space-y-6" data-testid="social-insights-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg bg-[#1A1A1A] border border-white/5 p-0.5">
              <button onClick={() => setSelectedNetwork('all')} className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', selectedNetwork === 'all' ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white')} data-testid="filter-all">Todas</button>
              {NETWORKS.map(n => (
                <button key={n.id} onClick={() => setSelectedNetwork(n.id)} className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', selectedNetwork === n.id ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white')} data-testid={`filter-${n.id}`}>{n.label}</button>
              ))}
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-insight-btn"><Plus size={18} className="mr-2" />Registrar Insight</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingId ? 'Editar Insight' : 'Registrar Insight de Red Social'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="grid grid-cols-3 gap-4">
                  {selectedClientId === 'all' && (
                    <div><Label className="text-zinc-300">Cliente</Label>
                      <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                        <SelectTrigger className="bg-[#1A1A1A] border-transparent"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label className="text-zinc-300">Red Social</Label>
                    <Select value={form.network} onValueChange={v => setForm({...form, network: v})}>
                      <SelectTrigger className="bg-[#1A1A1A] border-transparent"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">{NETWORKS.map(n => <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-zinc-300">Periodo</Label><Input type="month" value={form.period} onChange={e => setForm({...form, period: e.target.value})} required className="bg-[#1A1A1A] border-transparent" data-testid="insight-period" /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><Label className="text-zinc-400 text-xs">Seguidores</Label><Input type="number" value={form.followers} onChange={e => setForm({...form, followers: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Nuevos Seguidores</Label><Input type="number" value={form.new_followers} onChange={e => setForm({...form, new_followers: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Alcance</Label><Input type="number" value={form.reach} onChange={e => setForm({...form, reach: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Impresiones</Label><Input type="number" value={form.impressions} onChange={e => setForm({...form, impressions: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><Label className="text-zinc-400 text-xs">Engagement</Label><Input type="number" value={form.engagement} onChange={e => setForm({...form, engagement: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Tasa Engagement (%)</Label><Input type="number" step="0.01" value={form.engagement_rate} onChange={e => setForm({...form, engagement_rate: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Likes</Label><Input type="number" value={form.likes} onChange={e => setForm({...form, likes: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Comentarios</Label><Input type="number" value={form.comments} onChange={e => setForm({...form, comments: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><Label className="text-zinc-400 text-xs">Compartidos</Label><Input type="number" value={form.shares} onChange={e => setForm({...form, shares: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Clicks</Label><Input type="number" value={form.clicks} onChange={e => setForm({...form, clicks: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Visitas al Perfil</Label><Input type="number" value={form.profile_visits} onChange={e => setForm({...form, profile_visits: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-400 text-xs">Posts Publicados</Label><Input type="number" value={form.posts_published} onChange={e => setForm({...form, posts_published: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-insight-btn">{editingId ? 'Actualizar' : 'Guardar Insight'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Network Overview Cards */}
        {networkAggregates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {networkAggregates.map(net => (
              <Card key={net.id} className={cn('border', net.bgClass)} data-testid={`network-card-${net.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold" style={{ color: net.color }}>{net.label}</h3>
                    <span className="text-[10px] text-zinc-500">{net.latest?.period}</span>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{formatNum(net.latest?.followers)}</div>
                  <p className="text-xs text-zinc-500">seguidores</p>
                  {net.latest?.new_followers > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-green-400 text-xs">
                      <TrendingUp size={12} />+{formatNum(net.latest.new_followers)} nuevos
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
                    <div><p className="text-[10px] text-zinc-500">Alcance</p><p className="text-sm font-semibold text-white">{formatNum(net.latest?.reach)}</p></div>
                    <div><p className="text-[10px] text-zinc-500">Engagement</p><p className="text-sm font-semibold text-white">{net.latest?.engagement_rate || 0}%</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detailed Records */}
        <div className="space-y-3">
          {filteredInsights.length === 0 ? (
            <Card className="bg-[#141414] border-white/5">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <BarChart3 className="text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-400">No hay insights registrados</p>
                <p className="text-zinc-500 text-sm mt-1">Agrega metricas de redes sociales para tus clientes</p>
              </CardContent>
            </Card>
          ) : filteredInsights.map(insight => {
            const net = NETWORKS.find(n => n.id === insight.network);
            const clientName = clients.find(c => c.client_id === insight.client_id)?.name || '';
            return (
              <Card key={insight.insight_id} className="bg-[#141414] border-white/5 hover:border-white/10 transition-colors" data-testid={`insight-row-${insight.insight_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${net?.color}20` }}>
                        <span className="text-xs font-bold" style={{ color: net?.color }}>{net?.label?.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-white">{net?.label} — {clientName}</h3>
                        <p className="text-xs text-zinc-500">{insight.period}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(insight)} className="text-zinc-400 hover:text-white h-7 w-7"><Pencil size={13} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(insight.insight_id)} className="text-zinc-400 hover:text-red-400 h-7 w-7"><Trash2 size={13} /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                    {[
                      { label: 'Seguidores', value: insight.followers, icon: Users },
                      { label: 'Alcance', value: insight.reach, icon: Eye },
                      { label: 'Impresiones', value: insight.impressions, icon: Eye },
                      { label: 'Engagement', value: insight.engagement, icon: Heart },
                      { label: 'Tasa (%)', value: `${insight.engagement_rate || 0}%` },
                      { label: 'Likes', value: insight.likes, icon: Heart },
                      { label: 'Comentarios', value: insight.comments, icon: MessageCircle },
                      { label: 'Posts', value: insight.posts_published },
                    ].map((m, i) => (
                      <div key={i} className="text-center">
                        <p className="text-[10px] text-zinc-500 mb-0.5">{m.label}</p>
                        <p className="text-sm font-semibold text-white">{typeof m.value === 'number' ? formatNum(m.value) : m.value || '0'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SocialInsightsPage;
